import { useApolloClient, type DocumentNode } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const MAX_AUTOMATIC_PAGES = 3;

export interface CompanyConnection<T> {
  edges: { node: T }[];
  pageInfo: { hasNextPage: boolean; endCursor?: string | null };
  coverage?: string;
  available?: boolean;
  throughSequence?: number | null;
}

/** Company reads never render the previous query's cache or late responses. Sparse pages advance in bounded bursts. */
export function useCompanyConnection<T extends { id: string }>({
  query,
  variables,
  field,
  skip = false,
  refresh = 0,
}: {
  query: DocumentNode;
  variables: Record<string, unknown>;
  field: string;
  skip?: boolean;
  refresh?: number;
}) {
  const client = useApolloClient();
  const key = JSON.stringify([field, variables, refresh, skip]);
  const stableVariables = useMemo(() => variables, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const generation = useRef(0);
  const busy = useRef(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const [minimumRows, setMinimumRows] = useState(25);
  const [state, setState] = useState<{
    key: string;
    rows: T[];
    connection?: CompanyConnection<T>;
    loading: boolean;
    automaticPages?: number;
    error?: Error;
  }>({ key, rows: [], loading: !skip });
  const current: typeof state =
    state.key === key ? state : { key, rows: [], loading: !skip };

  const read = useCallback(
    async (after?: string | null, automatic = false) => {
      if (skip || busy.current) return;
      busy.current = true;
      const requestGeneration = generation.current;
      setState((previous) => ({
        ...previous,
        key,
        loading: true,
        error: undefined,
      }));
      try {
        const result = await client.query<Record<string, CompanyConnection<T>>>(
          {
            query,
            variables: {
              ...stableVariables,
              first: 25,
              after: after ?? undefined,
            },
            fetchPolicy: "no-cache",
          },
        );
        if (generation.current !== requestGeneration) return;
        const connection = result.data[field];
        if (!connection)
          throw new Error("This information is currently unavailable");
        if (
          after &&
          connection.pageInfo.hasNextPage &&
          connection.pageInfo.endCursor === after
        ) {
          throw new Error(
            "The next page could not be loaded. Refresh to try again.",
          );
        }
        setState((previous) => ({
          key,
          loading: false,
          automaticPages: automatic ? (previous.automaticPages ?? 0) + 1 : 0,
          connection,
          rows: [
            ...new Map(
              [
                ...(after && previous.key === key ? previous.rows : []),
                ...connection.edges.map(({ node }) => node),
              ].map((row) => [row.id, row]),
            ).values(),
          ],
        }));
      } catch (error) {
        if (generation.current === requestGeneration)
          setState((previous) => ({
            ...previous,
            key,
            loading: false,
            error:
              error instanceof Error
                ? error
                : new Error("This information is currently unavailable"),
          }));
      } finally {
        if (generation.current === requestGeneration) busy.current = false;
      }
    },
    [client, field, key, query, skip, stableVariables],
  );

  useEffect(() => {
    generation.current += 1;
    busy.current = false;
    setState({ key, rows: [], loading: !skip });
    if (!skip) void read();
    return () => {
      generation.current += 1;
      busy.current = false;
    };
  }, [key, read, skip]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() =>
      setMinimumRows(Math.max(25, Math.ceil(viewport.clientHeight / 32) + 1)),
    );
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  const loadMore = useCallback(() => {
    if (
      !current.loading &&
      !current.error &&
      current.connection?.pageInfo.hasNextPage
    ) {
      void read(current.connection.pageInfo.endCursor);
    }
  }, [current.loading, current.error, current.connection, read]);

  const paused =
    !current.loading &&
    !current.error &&
    current.rows.length < minimumRows &&
    !!current.connection?.pageInfo.hasNextPage &&
    (current.automaticPages ?? 0) >= MAX_AUTOMATIC_PAGES;

  useEffect(() => {
    if (
      !skip &&
      !current.loading &&
      !current.error &&
      current.rows.length < minimumRows &&
      current.connection?.pageInfo.hasNextPage &&
      (current.automaticPages ?? 0) < MAX_AUTOMATIC_PAGES
    ) {
      void read(current.connection.pageInfo.endCursor, true);
    }
  }, [current, minimumRows, read, skip]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const node = event.currentTarget;
      if (
        !paused &&
        node.scrollHeight - node.scrollTop - node.clientHeight < 240
      )
        loadMore();
    },
    [loadMore, paused],
  );

  return {
    rows: current.rows,
    loading: current.loading && !current.rows.length,
    isFetchingMore: current.loading && !!current.rows.length,
    error: current.error,
    connection: current.connection,
    handleScroll,
    viewportRef,
    loadMore,
    paused,
    retry: () => void read(current.connection?.pageInfo.endCursor),
  };
}
